import { useNavigationViewport } from "components/scrapEyeDataDashboard/navigation/useNavigationViewport";
import { stringSetQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { datasetSplitsQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { stringListQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { optionalTextQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { textQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  Autocomplete,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { usePercentSerialiser } from "hooks/serialisers/percent";
import {
  useGetModelCoverageConfigQuery,
  useGetScrapEyeMetadataQuery,
} from "src/store/api/generatedApi";
import DateTimeControls from "../components/DateTimeControls";
import { useModelPerformanceData } from "./hooks/useModelPerformanceData";
import { aggregateBuckets } from "./hooks/aggregateBuckets";
import { ModelPerformanceTimeline } from "./ModelPerformanceTimeline";
import { PipelineLevelFilter } from "./PipelineLevelFilter";
import {
  MISSING_DATA_KINDS,
  missingDataKindKey,
  type MissingDataKind,
} from "./pipelineLevel";
import {
  computeBucketSizeSeconds,
  SECONDS_PER_DAY,
  SECONDS_PER_WEEK,
} from "./hooks/bucketSizeUtils";
import { ModelComparisonSection } from "./modelComparison/ModelComparisonSection";
import {
  type DatasetSplit,
  filterBucketsBySplit,
  splitLabelKey,
} from "./hooks/filterBucketsBySplit";

dayjs.extend(utc);

export const ModelPerformanceTab = () => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const { format: formatPercent } = usePercentSerialiser({
    decimalPlaces: 1,
    includeSymbol: true,
    defaultText: "—",
  });
  const { start, end, pickerValue, handleRangeChange } = useNavigationViewport(
    "performanceRange",
    7
  );

  const {
    data: configData,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useGetModelCoverageConfigQuery({ tenantName: tenant });

  const availableLocations = useMemo(
    () => configData?.available_locations ?? [],
    [configData?.available_locations]
  );
  const modelsByLocation = useMemo(
    () => configData?.available_models ?? {},
    [configData?.available_models]
  );

  const [requestedLocation, setRequestedLocation] = useNavigationQuery(
    "performanceLocation",
    "",
    textQuery,
    "replace"
  );

  const defaultLocation = configData?.target_location ?? "";
  const selectedLocation = requestedLocation || defaultLocation;

  const availableModels = useMemo(
    () => (selectedLocation ? (modelsByLocation[selectedLocation] ?? []) : []),
    [modelsByLocation, selectedLocation]
  );

  const [selectedLabellingName, setSelectedLabellingName] = useNavigationQuery(
    "performanceModel",
    null,
    optionalTextQuery,
    "replace"
  );
  const [selectedAreas, setSelectedAreas] = useNavigationQuery(
    "performanceAreas",
    [],
    stringListQuery,
    "replace"
  );
  const [selectedSplits, setSelectedSplits] = useNavigationQuery(
    "performanceSplits",
    [],
    datasetSplitsQuery,
    "replace"
  );
  const [displayMode, setDisplayMode] = useNavigationQuery(
    "performanceDisplay",
    "rate",
    choiceQuery(["rate", "count"] as const),
    "replace"
  );
  const [aggregation, setAggregation] = useNavigationQuery(
    "performanceAggregation",
    "auto",
    choiceQuery(["auto", "daily", "weekly"] as const),
    "replace"
  );

  const { data: scrapEyeMetadata } = useGetScrapEyeMetadataQuery({
    tenantName: tenant,
  });
  const availableAreas = scrapEyeMetadata?.available_areas ?? [];

  const handleBarClick = useCallback(
    (bucketStart: Date) => {
      if (!selectedLocation) return;
      const dateStr = dayjs.utc(bucketStart).format("YYYY-MM-DD");
      const url = `/${tenant}/tool/view-labels/${selectedLocation}?date=${dateStr}`;
      window.open(url, "_blank");
    },
    [tenant, selectedLocation]
  );

  const bucketSizeSeconds = useMemo(() => {
    if (aggregation === "weekly") return SECONDS_PER_WEEK;
    if (aggregation === "daily") return SECONDS_PER_DAY;
    return computeBucketSizeSeconds(start.getTime(), end.getTime());
  }, [start, end, aggregation]);

  const {
    buckets: rawBuckets,
    isLoading: isLoadingData,
    splitPeriods,
  } = useModelPerformanceData(
    start,
    end,
    selectedLocation,
    selectedLabellingName,
    selectedAreas
  );

  const buckets = useMemo(
    () => aggregateBuckets(rawBuckets, bucketSizeSeconds),
    [rawBuckets, bucketSizeSeconds]
  );

  const splitFilteredBuckets = useMemo(
    () => filterBucketsBySplit(buckets, splitPeriods, selectedSplits),
    [buckets, splitPeriods, selectedSplits]
  );

  const [enabledKinds, setEnabledKinds] = useNavigationQuery(
    "performanceKinds",
    new Set(MISSING_DATA_KINDS.map(missingDataKindKey)),
    stringSetQuery,
    "replace"
  );

  const toggleKind = useCallback(
    (kind: MissingDataKind) => {
      const key = missingDataKindKey(kind);
      setEnabledKinds((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [setEnabledKinds]
  );

  const countsByKind = useMemo(() => {
    const counts = Object.fromEntries(
      MISSING_DATA_KINDS.map((k) => [missingDataKindKey(k), { fn: 0, fp: 0 }])
    );
    for (const bucket of splitFilteredBuckets) {
      const key = missingDataKindKey(bucket.missing_data_kind ?? null);
      const entry = counts[key];
      if (entry) {
        entry.fn += bucket.total_verified_events - bucket.matched_events;
        entry.fp += bucket.total_labeller_events - bucket.matched_events;
      }
    }
    return counts;
  }, [splitFilteredBuckets]);

  const filteredBuckets = useMemo(
    () =>
      splitFilteredBuckets.filter((b) =>
        enabledKinds.has(missingDataKindKey(b.missing_data_kind ?? null))
      ),
    [splitFilteredBuckets, enabledKinds]
  );

  const summary = useMemo(() => {
    if (filteredBuckets.length === 0) return null;
    const totalVerified = filteredBuckets.reduce(
      (s, b) => s + b.total_verified_events,
      0
    );
    const totalMatched = filteredBuckets.reduce(
      (s, b) => s + b.matched_events,
      0
    );
    const totalLabeller = filteredBuckets.reduce(
      (s, b) => s + b.total_labeller_events,
      0
    );
    const totalFn = totalVerified - totalMatched;
    const totalFp = totalLabeller - totalMatched;
    const overallFnRate = totalVerified > 0 ? totalFn / totalVerified : null;
    const overallFpRate = totalLabeller > 0 ? totalFp / totalLabeller : null;
    const jaccardDenominator = totalMatched + totalFn + totalFp;
    const overallJaccard =
      jaccardDenominator > 0 ? totalMatched / jaccardDenominator : null;
    return {
      totalVerified,
      totalMatched,
      totalLabeller,
      overallFnRate,
      overallFpRate,
      overallJaccard,
    };
  }, [filteredBuckets]);

  if (isLoadingConfig) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isConfigError) {
    return (
      <Stack p={2}>
        <Alert severity="error">{t("modelCoverageConfigError")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack p={2} spacing={3}>
      <DateTimeControls
        pickerValue={pickerValue}
        onRangeChange={handleRangeChange}
      />

      <Stack
        spacing={2}
        sx={{
          p: 3,
          backgroundColor: "grey.50",
          borderRadius: 2,
          border: 1,
          borderColor: "grey.300",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            variant="h5"
            fontWeight="fontWeightSemiBold"
            color="text.primary"
          >
            {t("modelPerformanceTitle")}
          </Typography>

          {availableLocations.length > 1 ? (
            <Select
              size="small"
              value={selectedLocation}
              onChange={(event) => {
                setRequestedLocation(
                  event.target.value === defaultLocation
                    ? ""
                    : event.target.value
                );
                setSelectedLabellingName(null);
                setSelectedSplits([]);
              }}
            >
              {availableLocations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </Select>
          ) : null}

          <Select
            size="small"
            value={selectedLabellingName ?? ""}
            displayEmpty
            onChange={(event) => {
              setSelectedLabellingName(event.target.value || null);
              setSelectedSplits([]);
            }}
          >
            <MenuItem value="">{t("modelPerformanceSelectModel")}</MenuItem>
            {availableModels.map((model) => (
              <MenuItem key={model.labelling_name} value={model.labelling_name}>
                {model.labelling_name}
              </MenuItem>
            ))}
          </Select>

          {availableAreas.length > 0 ? (
            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              disableCloseOnSelect
              options={availableAreas}
              value={selectedAreas}
              onChange={(_, value) => {
                setSelectedAreas(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedAreas.length === 0
                      ? t("modelPerformanceAllAreas")
                      : undefined
                  }
                  sx={{ minWidth: 200 }}
                />
              )}
            />
          ) : null}
          <Autocomplete
            multiple
            size="small"
            limitTags={2}
            disableCloseOnSelect
            disabled={splitPeriods.length === 0}
            options={["train", "val", "test"] as DatasetSplit[]}
            getOptionLabel={(option) => t(splitLabelKey(option))}
            value={selectedSplits}
            onChange={(_, value) => {
              setSelectedSplits(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedSplits.length === 0 ? t("datasetSplitAll") : undefined
                }
                sx={{ minWidth: 200 }}
              />
            )}
          />
          <Select
            size="small"
            value={aggregation}
            onChange={(event) => {
              setAggregation(event.target.value);
            }}
          >
            <MenuItem value="auto">{t("modelPerformancePerEvent")}</MenuItem>
            <MenuItem value="daily">{t("modelPerformancePerDay")}</MenuItem>
            <MenuItem value="weekly">{t("modelPerformancePerWeek")}</MenuItem>
          </Select>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={displayMode}
            onChange={(_, value: "rate" | "count" | null) => {
              if (value) setDisplayMode(value);
            }}
          >
            <ToggleButton value="rate">
              {t("modelPerformanceRate")}
            </ToggleButton>
            <ToggleButton value="count">
              {t("modelPerformanceCount")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {!selectedLabellingName ? (
          <Alert severity="info">{t("modelPerformanceSelectModel")}</Alert>
        ) : isLoadingData ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={24} />
          </Stack>
        ) : buckets.length === 0 ? (
          <Alert severity="info">{t("modelPerformanceNoData")}</Alert>
        ) : (
          <Stack spacing={2}>
            <PipelineLevelFilter
              enabledKinds={enabledKinds}
              onToggle={toggleKind}
              countsByKind={countsByKind}
            />
            {summary ? (
              <Stack direction="row" spacing={4}>
                <SummaryChip
                  label={t("falseNegativeRate")}
                  value={formatPercent(
                    summary.overallFnRate !== null
                      ? summary.overallFnRate * 100
                      : undefined
                  )}
                  color={
                    summary.overallFnRate !== null &&
                    summary.overallFnRate > 0.1
                      ? "error.main"
                      : undefined
                  }
                />
                <SummaryChip
                  label={t("falsePositiveRate")}
                  value={formatPercent(
                    summary.overallFpRate !== null
                      ? summary.overallFpRate * 100
                      : undefined
                  )}
                  color={
                    summary.overallFpRate !== null &&
                    summary.overallFpRate > 0.1
                      ? "warning.main"
                      : undefined
                  }
                />
                <SummaryChip
                  label={t("jaccardAccuracy")}
                  value={formatPercent(
                    summary.overallJaccard !== null
                      ? summary.overallJaccard * 100
                      : undefined
                  )}
                />
              </Stack>
            ) : null}

            <ModelPerformanceTimeline
              buckets={filteredBuckets}
              start={start}
              end={end}
              displayMode={displayMode}
              onBarClick={handleBarClick}
            />
          </Stack>
        )}
      </Stack>

      <ModelComparisonSection />
    </Stack>
  );
};

const SummaryChip = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Stack alignItems="center">
    <Typography
      variant="h6"
      fontWeight="fontWeightBold"
      color={color ?? "text.primary"}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);
